import { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { BarChart3, Trophy, AlertTriangle, Download, FileText, Loader2, ArrowLeft, Users, Mail } from "lucide-react";

const download = async (url, filename) => {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    toast.error("Erro ao exportar.");
  }
};

function Detail({ sessionId, onBack }) {
  const [d, setD] = useState(null);
  const [emailing, setEmailing] = useState(false);
  useEffect(() => { api.get(`/reports/games/${sessionId}`).then((r) => setD(r.data)).catch(() => {}); }, [sessionId]);
  if (!d) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;

  const emailReport = async () => {
    setEmailing(true);
    try {
      const res = await api.post(`/reports/games/${sessionId}/email`);
      toast.success(`Relatório enviado para ${res.data.sent_to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar o relatório.");
    } finally {
      setEmailing(false);
    }
  };

  const chartData = d.question_stats.map((q, i) => ({ name: `Q${i + 1}`, acuracia: q.accuracy }));

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-brand-muted hover:text-brand-ink mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Voltar</button>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-black text-brand-ink">{d.title}</h2>
          <p className="text-brand-muted text-sm">{new Date(d.created_at).toLocaleString("pt-BR")}{d.turma ? ` · Turma: ${d.turma}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={emailReport} disabled={emailing} className="rounded-full gap-2 bg-brand-blue hover:bg-blue-700" data-testid="email-report">
            {emailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Enviar por e-mail
          </Button>
          <Button variant="outline" onClick={() => download(`/reports/games/${sessionId}/export.csv`, `relatorio_${sessionId}.csv`)} className="rounded-full gap-2" data-testid="export-csv"><Download className="w-4 h-4" /> CSV</Button>
          <Button variant="outline" onClick={() => download(`/reports/games/${sessionId}/export.pdf`, `relatorio_${sessionId}.pdf`)} className="rounded-full gap-2" data-testid="export-pdf"><FileText className="w-4 h-4" /> PDF</Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Participantes", value: d.participant_count, icon: Users },
          { label: "Média de acertos", value: `${d.avg_accuracy}%`, icon: Trophy },
          { label: "Pontuação média", value: d.avg_score, icon: BarChart3 },
          { label: "Tempo médio", value: `${d.avg_time}s`, icon: BarChart3 },
        ].map((c) => (
          <div key={c.label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
            <p className="font-heading text-2xl font-black text-brand-ink">{c.value}</p>
            <p className="text-xs text-brand-muted mt-1">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading font-bold text-brand-ink mb-4">Acurácia por questão (%)</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="acuracia" radius={[8, 8, 0, 0]}>
                {chartData.map((e, i) => (
                  <Cell key={i} fill={e.acuracia >= 60 ? "#10B981" : e.acuracia >= 30 ? "#F59E0B" : "#EF4444"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading font-bold text-brand-ink flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4 text-brand-yellow" /> Mais erradas</h3>
          <ul className="space-y-3">
            {d.most_missed.map((q) => (
              <li key={q.index} className="text-sm">
                <p className="text-brand-ink line-clamp-2">{q.question}</p>
                <p className="text-xs text-red-500 font-semibold">{q.accuracy}% de acerto</p>
              </li>
            ))}
            {!d.most_missed.length && <p className="text-sm text-brand-muted">Sem dados suficientes.</p>}
          </ul>
        </div>
      </div>

      <div className="mt-6 bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <h3 className="font-heading font-bold text-brand-ink flex items-center gap-2 mb-4"><Trophy className="w-4 h-4 text-brand-yellow" /> Ranking</h3>
        <div className="space-y-2">
          {d.ranking.map((p, i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-100 pb-2 text-sm">
              <span className="font-medium text-brand-ink">{i + 1}. {p.nickname}</span>
              <span className="text-brand-muted">{p.correct}/{d.total_questions} acertos</span>
              <span className="font-heading font-bold text-brand-ink">{p.score}</span>
            </div>
          ))}
          {!d.ranking.length && <p className="text-sm text-brand-muted">Nenhum participante.</p>}
        </div>
      </div>
    </div>
  );
}

export default function Resultados() {
  const [games, setGames] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/reports/games").then((r) => setGames(r.data)).catch(() => setGames([])); }, []);

  if (selected) return <DashboardLayout><Detail sessionId={selected} onBack={() => setSelected(null)} /></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><BarChart3 className="w-6 h-6 text-brand-green" /></div>
        <div>
          <h1 className="font-heading text-3xl font-black text-brand-ink">Resultados e relatórios</h1>
          <p className="text-brand-muted text-sm">Desempenho dos seus jogos</p>
        </div>
      </div>

      {!games ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>
      ) : games.length === 0 ? (
        <div className="mt-16 text-center text-brand-muted">
          <BarChart3 className="w-12 h-12 mx-auto text-slate-300" />
          <p className="mt-4">Nenhum jogo realizado ainda. Inicie um jogo a partir de um quiz.</p>
        </div>
      ) : (
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.map((g) => (
            <button key={g.session_id} onClick={() => setSelected(g.session_id)} className="text-left bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow" data-testid={`report-${g.session_id}`}>
              <div className="flex items-center justify-between">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${g.status === "ended" ? "bg-emerald-50 text-brand-green" : "bg-blue-50 text-brand-blue"}`}>{g.status === "ended" ? "Finalizado" : "Em andamento"}</span>
                <span className="text-xs text-brand-muted">{new Date(g.created_at).toLocaleDateString("pt-BR")}</span>
              </div>
              <h3 className="mt-4 font-heading font-bold text-lg text-brand-ink line-clamp-2">{g.title}</h3>
              <p className="mt-2 text-sm text-brand-muted flex items-center gap-1.5"><Users className="w-4 h-4" /> {g.participant_count} participantes · PIN {g.pin}</p>
            </button>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
